import { z } from "zod";
import type { AnalyteKind } from "./catalogue.js";

/**
 * Cultures (urocultivo, coprocultivo, exudados…), ADR 0019.
 *
 * A culture study is recognised by its structure, the way the lab's catalogue already builds
 * them: a `document` analyte (the culture's report) plus antibiotic rows, i.e. value analytes
 * whose answer list reads SENSIBLE / INTERMEDIO / RESISTENTE. Nothing in the catalogue changes.
 *
 * Per order, the chemist records the outcome on the document row (negative, or positive with the
 * organism(s) and the count) and chooses which antibiotics apply: an antibiotic with an answer is
 * chosen, one without is not. Chosen antibiotics print in the study's order; a negative culture
 * prints no antibiogram. The study's snapshot (ADR 0009) is never touched.
 */

/**
 * Gram classification of an isolate (ADR 0026, pilot request 2026-11-03): it decides which of the
 * laboratory's two antibiotic panels preloads the isolate's antibiogram. "none" = not applicable
 * (yeasts, fungi): no panel. Absent = not classified, the ADR 0019 behaviour.
 */
export const Gram = z.enum(["positive", "negative", "none"]);
export type Gram = z.infer<typeof Gram>;
/** The two classifications that carry a panel. */
export const PanelGram = z.enum(["positive", "negative"]);
export type PanelGram = z.infer<typeof PanelGram>;

/** As printed and as offered on screen. */
export const GRAM_LABEL: Record<Gram, string> = { positive: "Gram positivo", negative: "Gram negativo", none: "No aplica" };
/** A panel's name in Catálogo and in the picker. */
export const PANEL_LABEL: Record<PanelGram, string> = { positive: "Gram positivos", negative: "Gram negativos" };

/**
 * The answers of a panel antibiogram (Kirby-Bauer as the pilot lab writes it: three grades of
 * sensitivity, intermediate, resistant). Study antibiotics (ADR 0019) keep their own answer lists.
 */
export const SUSCEPTIBILITY_ANSWERS = ["SENSIBLE (+)", "SENSIBLE (++)", "SENSIBLE (+++)", "INTERMEDIO", "RESISTENTE"] as const;
export const SusceptibilityAnswer = z.enum(SUSCEPTIBILITY_ANSWERS);
export type SusceptibilityAnswer = z.infer<typeof SusceptibilityAnswer>;

export const AntibioticName = z.string().trim().min(1).max(120);
export const ANTIBIOGRAM_MAX = 60;

/** Case- and accent-insensitive identity of an antibiotic or organism name. */
export function nameKey(s: string): string {
  return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, " ").trim().toLowerCase();
}

const uniqueNames = (xs: string[]) => new Set(xs.map(nameKey)).size === xs.length;

/**
 * One antibiotic of an isolate's antibiogram. The name is frozen as written when it was chosen
 * (editing a panel later never changes an order, ADR 0009). `result` null = chosen, not answered
 * yet: kept so a half-done antibiogram survives a save, never printed.
 */
export const AntibiogramEntry = z.object({
  antibiotic: AntibioticName,
  result: SusceptibilityAnswer.nullable().default(null),
});
export type AntibiogramEntry = z.infer<typeof AntibiogramEntry>;

export const CultureIsolate = z
  .object({
    organism: z.string().trim().min(1).max(120),
    /** As the lab writes it: "18 000 UFC/mL", "> 100 000 UFC/mL". */
    count: z.string().trim().max(60).nullable().default(null),
    /** ADR 0026; absent on every culture captured before it. */
    gram: Gram.optional(),
    /** The isolate's own antibiogram, in print order (ADR 0026); only on a Gram positive/negative isolate. */
    antibiogram: z.array(AntibiogramEntry).max(ANTIBIOGRAM_MAX).optional(),
  })
  .superRefine((iso, ctx) => {
    if (!iso.antibiogram?.length) return;
    if (iso.gram !== "positive" && iso.gram !== "negative") {
      ctx.addIssue({ code: "custom", message: "El antibiograma de un microorganismo va con su Gram (positivo o negativo).", path: ["antibiogram"] });
    }
    if (!uniqueNames(iso.antibiogram.map((e) => e.antibiotic))) {
      ctx.addIssue({ code: "custom", message: "Un antibiótico aparece dos veces en el antibiograma.", path: ["antibiogram"] });
    }
  });
export type CultureIsolate = z.infer<typeof CultureIsolate>;

export const CultureResult = z
  .object({
    outcome: z.enum(["negative", "positive"]),
    isolates: z.array(CultureIsolate).max(3).default([]),
  })
  .refine((c) => (c.outcome === "positive" ? c.isolates.length > 0 : c.isolates.length === 0), {
    message: "Un cultivo positivo lleva al menos un microorganismo; uno negativo, ninguno.",
    path: ["isolates"],
  });
export type CultureResult = z.infer<typeof CultureResult>;

/**
 * The culture as stored: an isolate without a Gram keeps exactly the ADR 0019 shape (no `gram`,
 * no `antibiogram` key), and an empty antibiogram is no antibiogram. So a culture captured the old
 * way hashes the same as before (packages/db/src/hashing.ts).
 */
export function normalizeCulture(c: CultureResult): CultureResult {
  return {
    outcome: c.outcome,
    isolates: c.isolates.map((i) => {
      const out: CultureIsolate = { organism: i.organism, count: i.count };
      if (i.gram) out.gram = i.gram;
      if (i.antibiogram?.length && (i.gram === "positive" || i.gram === "negative")) out.antibiogram = i.antibiogram.map((e) => ({ antibiotic: e.antibiotic, result: e.result }));
      return out;
    }),
  };
}

/** The answered entries of an isolate's antibiogram, in its order: what the report prints. */
export function answeredAntibiogram(iso: Pick<CultureIsolate, "antibiogram">): { antibiotic: string; result: SusceptibilityAnswer }[] {
  return (iso.antibiogram ?? []).filter((e): e is { antibiotic: string; result: SusceptibilityAnswer } => e.result !== null);
}

/** The laboratory's two antibiotic lists, in print order (Catálogo → Antibióticos, ADR 0026). */
export const AntibioticPanels = z.object({
  positive: z.array(AntibioticName).max(ANTIBIOGRAM_MAX).default([]),
  negative: z.array(AntibioticName).max(ANTIBIOGRAM_MAX).default([]),
});
export type AntibioticPanels = z.infer<typeof AntibioticPanels>;
export const EMPTY_ANTIBIOTIC_PANELS: AntibioticPanels = { positive: [], negative: [] };

/** `catalogue.antibiotic_panel.set`: the whole list of one panel, in print order. */
export const AntibioticPanelSetPayload = z.object({
  gram: PanelGram,
  antibiotics: z.array(AntibioticName).max(ANTIBIOGRAM_MAX).refine(uniqueNames, "Un antibiótico aparece dos veces en la lista."),
});
export type AntibioticPanelSetPayload = z.infer<typeof AntibioticPanelSetPayload>;

/**
 * The antibiogram a Gram preloads: the panel's antibiotics in the panel's order, every one
 * chosen and unanswered, except that an answer already given to the same antibiotic is kept.
 */
export function antibiogramFromPanel(panel: readonly string[], current: readonly AntibiogramEntry[] = []): AntibiogramEntry[] {
  const answered = new Map(current.map((e) => [nameKey(e.antibiotic), e.result]));
  return panel.map((antibiotic) => ({ antibiotic, result: answered.get(nameKey(antibiotic)) ?? null }));
}

/**
 * Entries sorted by a reference order (the isolate's own panel, then the other one, then the
 * study's antibiotics): what «Agregar o quitar» adds lands where it prints. Names in no list keep
 * their relative place after the known ones.
 */
export function orderAntibiogram(entries: readonly AntibiogramEntry[], reference: readonly string[]): AntibiogramEntry[] {
  const pos = new Map<string, number>();
  reference.forEach((n, i) => {
    const k = nameKey(n);
    if (!pos.has(k)) pos.set(k, i);
  });
  return entries
    .map((e, i) => ({ e, i, p: pos.get(nameKey(e.antibiotic)) ?? Number.MAX_SAFE_INTEGER }))
    .sort((a, b) => a.p - b.p || a.i - b.i)
    .map((x) => x.e);
}

/** An answer list that is an antibiogram's (SENSIBLE (+), INTERMEDIO, RESISTENTE…). */
const SUSCEPTIBILITY = /\b(SENSIBLE|INTERMEDI[OA]|RESISTENTE)\b/i;

/** A section that heads an antibiogram ("ANTIBIOGRAMA Y SUSCEPTIBILIDAD MICROBIANA"…). */
const ANTIBIOGRAM_SECTION = /ANTIBIOGRAMA|SUSCEPTIBILIDAD|SENSIBILIDAD MICROBIANA/i;

/**
 * An antibiotic row: a text/reference analyte whose answer list is an antibiogram's, or (pilot
 * report 2026-11-03 09:03) one with no answer list under an antibiogram section. Orders frozen
 * before the laboratory's answer lists were loaded have antibiotics without options in their
 * snapshot; they are still antibiotics and still get the culture capture (ADR 0026 follow-up).
 */
export function isAntibioticAnalyte(a: { kind: AnalyteKind; options: string[] | null; section?: string | null }): boolean {
  if (a.kind !== "text" && a.kind !== "reference") return false;
  const options = a.options ?? [];
  if (options.some((o) => SUSCEPTIBILITY.test(o))) return true;
  return options.length === 0 && ANTIBIOGRAM_SECTION.test(a.section ?? "");
}

/** The answers an antibiotic row offers: its own list when it has one, else the fixed vocabulary. Never typed. */
export function antibioticAnswers(a: { options: string[] | null }): string[] {
  const own = (a.options ?? []).map((o) => o.trim()).filter((o) => o !== "");
  return own.length ? own : [...SUSCEPTIBILITY_ANSWERS];
}

export interface CultureShape<T> {
  /** The document analyte that carries the outcome and the culture's report. */
  document: T;
  /** Antibiotic analytes, in the order given (the study's order). Optional per order. */
  antibiotics: T[];
}

/** The culture structure of a study's analytes, or null when the study is not a culture. */
export function cultureShape<T extends { kind: AnalyteKind; options: string[] | null; section?: string | null }>(analytes: T[]): CultureShape<T> | null {
  const document = analytes.find((a) => a.kind === "document");
  const antibiotics = analytes.filter(isAntibioticAnalyte);
  return document && antibiotics.length > 0 ? { document, antibiotics } : null;
}

/** Organisms offered before the lab has recorded its own (it learns from every capture). */
export const COMMON_ORGANISMS = [
  "Escherichia coli",
  "Klebsiella pneumoniae",
  "Proteus mirabilis",
  "Enterobacter cloacae",
  "Enterococcus faecalis",
  "Pseudomonas aeruginosa",
  "Staphylococcus aureus",
  "Staphylococcus saprophyticus",
  "Staphylococcus epidermidis",
  "Streptococcus agalactiae",
  "Streptococcus pyogenes",
  "Candida albicans",
] as const;

/**
 * The Gram of the common organisms, offered before the laboratory has classified them itself
 * (the laboratory's own latest classification of an organism wins, ADR 0026).
 */
export const COMMON_ORGANISM_GRAM: Readonly<Record<(typeof COMMON_ORGANISMS)[number], Gram>> = {
  "Escherichia coli": "negative",
  "Klebsiella pneumoniae": "negative",
  "Proteus mirabilis": "negative",
  "Enterobacter cloacae": "negative",
  "Enterococcus faecalis": "positive",
  "Pseudomonas aeruginosa": "negative",
  "Staphylococcus aureus": "positive",
  "Staphylococcus saprophyticus": "positive",
  "Staphylococcus epidermidis": "positive",
  "Streptococcus agalactiae": "positive",
  "Streptococcus pyogenes": "positive",
  "Candida albicans": "none",
};

/** The Gram to preselect for an organism: the lab's own (keyed by `nameKey`), else the common one. */
export function defaultGram(organism: string, labGrams: Readonly<Record<string, Gram>> = {}): Gram | null {
  const k = nameKey(organism);
  if (!k) return null;
  const own = labGrams[k];
  if (own) return own;
  const common = (Object.keys(COMMON_ORGANISM_GRAM) as (keyof typeof COMMON_ORGANISM_GRAM)[]).find((o) => nameKey(o) === k);
  return common ? COMMON_ORGANISM_GRAM[common] : null;
}

/**
 * One line for lists and previews: "Negativo" / "Positivo: Escherichia coli 18 000 UFC/mL", with
 * the Gram after the organism when it was classified ("Escherichia coli (Gram negativo) …").
 */
export function cultureSummary(c: CultureResult | null | undefined): string {
  if (!c) return "";
  if (c.outcome === "negative") return "Negativo";
  const one = (i: CultureIsolate) => [i.organism, i.gram && i.gram !== "none" ? `(${GRAM_LABEL[i.gram]})` : null, i.count].filter(Boolean).join(" ");
  return `Positivo: ${c.isolates.map(one).join("; ")}`;
}
